/**
 * Machine-local camera preference for the counter's loyalty-card scanner.
 *
 * A camera is physical to a given terminal, exactly like a receipt printer
 * (see stationPrinterMap.ts): a Surface till has a front AND a rear camera and
 * the rear one is the useful one — the customer holds their wallet card up to
 * the back of the machine — while the next tenant's till has a single webcam
 * whose deviceId means nothing here. So the choice belongs on the machine, not
 * in the shared tenant database. localStorage in the desktop app is per-origin
 * (the local server) and is wiped when the shell is re-pointed at another
 * tenant, so it never bleeds across restaurants.
 *
 * deviceIds are also not stable forever — they rotate when the browser's
 * storage is cleared and vanish when a USB camera is unplugged — so every
 * consumer must treat a saved id as a hint and fall back gracefully. See the
 * OverconstrainedError retry in CounterScanLoyaltyCard.
 */

const KEY = 'bhookly-scanner-camera'

export interface ScannerCameraPrefs {
  /** MediaDeviceInfo.deviceId of the camera to use, '' => let the browser pick. */
  deviceId: string
  /**
   * Whether opening the scan panel should start the viewfinder immediately.
   * Defaults to true; set false when staff explicitly close the camera on this
   * machine (a till that only ever uses its USB scanner shouldn't have a
   * webcam light come on with every guest lookup).
   */
  autoStart: boolean
}

const DEFAULTS: ScannerCameraPrefs = { deviceId: '', autoStart: true }

export function getScannerCamera(): ScannerCameraPrefs {
  if (typeof window === 'undefined') return { ...DEFAULTS }
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw) as Partial<ScannerCameraPrefs> | null
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULTS }
    return {
      deviceId: typeof parsed.deviceId === 'string' ? parsed.deviceId : '',
      autoStart: parsed.autoStart !== false,
    }
  } catch {
    return { ...DEFAULTS }
  }
}

function write(prefs: ScannerCameraPrefs): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs))
  } catch {
    // localStorage unavailable/full — non-fatal; the scanner just forgets the
    // choice and falls back to the browser's default camera next time.
  }
}

/** Pin the scanner to a specific camera on this machine ('' clears the pin). */
export function setScannerCameraId(deviceId: string): void {
  write({ ...getScannerCamera(), deviceId: deviceId || '' })
}

/** Remember whether the viewfinder should auto-start on this machine. */
export function setScannerAutoStart(autoStart: boolean): void {
  write({ ...getScannerCamera(), autoStart })
}

/** Forget everything — used when a saved deviceId turns out to be dead. */
export function clearScannerCamera(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}

/**
 * Video input devices on this machine.
 *
 * Labels are empty until camera permission has been granted at least once, so
 * callers that want readable names should enumerate AFTER getUserMedia has
 * resolved. Returns [] on any failure — an empty list is the signal "this
 * machine has no camera", which is how the counter decides not to auto-start.
 */
export async function listVideoInputs(): Promise<MediaDeviceInfo[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((d) => d.kind === 'videoinput')
  } catch {
    return []
  }
}

/** Human label for a camera, falling back to a positional name pre-permission. */
export function cameraLabel(device: MediaDeviceInfo, index: number): string {
  return device.label?.trim() || `Camera ${index + 1}`
}

/**
 * Whether this device can decode a QR from its camera in-browser.
 *
 * Deliberately does NOT test for `BarcodeDetector`. Chrome ships that API on
 * Android, macOS and ChromeOS only — on Chrome for Windows and Linux, and
 * therefore inside the Electron shell on every restaurant PC we deploy to, it
 * is undefined. Gating on it hid camera scanning from the entire till fleet
 * while working fine on the developer's Mac. The counter now falls back to a
 * bundled jsQR decoder, so the only real requirement is a camera stream, and
 * getUserMedia needs a secure context — https, or the loopback origin the
 * desktop shell serves from.
 */
export function cameraScanSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext !== false &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  )
}

/** Touch-primary device (phone/tablet) — where focusing an input opens a keyboard. */
export function isTouchPrimary(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(pointer: coarse)').matches
}

// ── Retail barcodes (kitchen flow, Lane A) ───────────────────────────────────
//
// The loyalty scanner only ever reads QR (jsQR, see loyaltyCardScanner.tsx).
// The store scans what is printed on a can, a bag or a carton: EAN-13/8,
// UPC-A/E and Code-128. Same split as the QR path — the browser's native
// BarcodeDetector where it exists, otherwise a bundled decoder — for the same
// reason: Chrome on Windows (the Electron till fleet) has no BarcodeDetector.
// The fallback here is @zxing/browser, lazily imported so it never lands in
// the main bundle.

/** Formats the store scans, in BarcodeDetector's names. */
export const RETAIL_BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'] as const

/** Decodes the barcode in front of the camera, or null if there isn't one. */
export type BarcodeDecode = (video: HTMLVideoElement) => Promise<string | null>

interface NativeBarcodeDetectorCtor {
  new (options?: { formats?: string[] }): {
    detect(source: CanvasImageSource): Promise<{ rawValue?: string; format?: string }[]>
  }
  getSupportedFormats?: () => Promise<string[]>
}

/** Long edge, in px, a frame is scaled to before the fallback decoder sees it. */
const BARCODE_DECODE_EDGE = 720

/**
 * Picks the native detector when it advertises at least one retail format
 * (asking only for the formats it supports — an unsupported one throws), and
 * @zxing/browser everywhere else. Linear barcodes need more pixels than a QR,
 * hence the larger decode edge than the loyalty scanner's.
 */
export async function createBarcodeDecoder(): Promise<BarcodeDecode> {
  const native =
    typeof window !== 'undefined'
      ? (window as unknown as { BarcodeDetector?: NativeBarcodeDetectorCtor }).BarcodeDetector
      : undefined
  if (native) {
    try {
      const supported = (await native.getSupportedFormats?.()) ?? []
      const formats = RETAIL_BARCODE_FORMATS.filter((f) => supported.includes(f))
      if (formats.length > 0) {
        const detector = new native({ formats: [...formats] })
        return async (video) => {
          const found = await detector.detect(video)
          return found.find((b) => b.rawValue?.trim())?.rawValue?.trim() ?? null
        }
      }
    } catch {
      // Probing failed — fall through to the bundled decoder.
    }
  }

  const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
    import('@zxing/browser'),
    import('@zxing/library'),
  ])
  const hints = new Map<unknown, unknown>([
    [
      DecodeHintType.POSSIBLE_FORMATS,
      [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128],
    ],
    [DecodeHintType.TRY_HARDER, true],
  ])
  const reader = new BrowserMultiFormatReader(hints as Map<never, unknown>)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  return async (video) => {
    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!ctx || !vw || !vh) return null
    const scale = Math.min(1, BARCODE_DECODE_EDGE / Math.max(vw, vh))
    const w = Math.max(1, Math.round(vw * scale))
    const h = Math.max(1, Math.round(vh * scale))
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
    ctx.drawImage(video, 0, 0, w, h)
    try {
      return reader.decodeFromCanvas(canvas).getText().trim() || null
    } catch {
      // NotFoundException on every frame without a barcode — expected.
      return null
    }
  }
}
